import { scopeAndImportantCSS } from "./css-xss";

export { scopeAndImportantCSS } from "./css-xss";

export type CSSPropertyValue = string | number | undefined | null;
export type CSSProperties = Record<string, CSSPropertyValue>;

/**
 * Converts a CSS properties object to an HTML style string
 * @param styles - Object containing CSS properties
 * @returns HTML style string (e.g., "color: red; font-size: 14px")
 */
export const stylesToString = (styles?: CSSProperties): string => {
  if (!styles) return "";
  
  return Object.entries(styles)
    .filter(([_, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => {
      // Convert camelCase to kebab-case
      const kebabKey = key.replace(/([A-Z])/g, "-$1").toLowerCase();
      // Escape quotes so values like font-family: "Open Sans" are safe in style=""
      const safeValue = String(value).replace(/"/g, "'");
      return `${kebabKey}: ${safeValue}`;
    })
    .join("; ");
};

/**
 * Escapes HTML special characters to prevent XSS
 * @param str - String to escape
 * @returns Escaped string
 */
export const escapeHtml = (str: string): string => {
  const div = document.createElement("div");
  div.textContent = str;
  return div.innerHTML;
};

export type HTMLAttributes = Record<string, string | number | boolean | CSSProperties | undefined | null>;

/**
 * Renders HTML attributes from an object
 * @param attrs - Object containing attribute key-value pairs
 * @returns HTML attribute string
 */
export const attrsToString = (attrs?: HTMLAttributes): string => {
  if (!attrs) return "";
  
  return Object.entries(attrs)
    .filter(([_, value]) => value !== undefined && value !== null && value !== false)
    .map(([key, value]) => {
      if (value === true) return key;
      if (key === "style" && typeof value === "object") {
        return `style="${stylesToString(value as CSSProperties)}"`;
      }
      if (key === "class" || key === "className") {
        return `class="${escapeAttr(value as string)}"`;
      }
      return `${key}="${String(value).replace(/"/g, "&quot;")}"`;
    })
    .join(" ");
};

/**
 * Renders an icon as HTML by fetching the SVG
 * @param name - Icon name
 * @returns HTML string for the icon
 */
export const renderIcon = (name?: string): string => {
  if (!name) return "";
  // Icon will be loaded by frontend script
  return ``;
};

/**
 * Escapes HTML special characters
 * @param str - String to escape
 * @returns Escaped string
 */
export const escapeAttr = (str?: string | number): string => {
  if (str === undefined || str === null) return "";
  return str.toString()
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
};

/**
 * Interface for link properties
 */
export interface LinkProps {
  url?: string;
  target?: string;
  rel?: string[];
  convertToAbsolute?: boolean;
  className?: string;
  style?: CSSProperties;
  id?: string;
  attrs?: Record<string, string>;
}

/**
 * Builds link attributes with proper escaping and URL conversion
 * @param props - Link properties
 * @returns Object with tag name and attributes string
 */
export const buildLinkAttrs = (props: LinkProps): { tag: 'a' | 'div'; attrs: string } => {
  const { url, target, rel, convertToAbsolute, className, style, id, attrs } = props;
  
  const baseStyle = style ? `style="${stylesToString(style)}"` : '';
  const classAttr = className ? `class="${escapeAttr(className)}"` : '';
  const idAttr = id ? `id="${escapeAttr(id)}"` : '';
  
  if (!url || url === "") {
    const attrs = [classAttr, baseStyle, idAttr].filter(Boolean).join(' ');
    return { tag: 'div', attrs };
  }

  let finalUrl = url;
  if (convertToAbsolute && !/^https?:\/\//.test(url)) {
    finalUrl = "https://" + url.replace(/^\//, "");
  }

  const relStr = rel?.join?.(" ") || "";
  const linkAttrs = [
    classAttr,
    baseStyle,
    idAttr,
    `href="${escapeAttr(finalUrl)}"`,
    target ? `target="${escapeAttr(target)}"` : '',
    relStr ? `rel="${escapeAttr(relStr)}"` : '',
    ...Object.entries(attrs || {}).map(([key, value]) => `${key}="${escapeAttr(value)}"`),
  ].filter(Boolean).join(' ');

  return { tag: 'a', attrs: linkAttrs };
};


export const applyCustomStyle = async (style?: string, tableId?: string, doc: Document = document) => {
  if (!style || !tableId || !doc) return;
  const el = doc.querySelector(`style#wptb-table-${tableId}`);
  if (el) {
    el.remove();
  }

  try {
    const scopedStyle = scopeAndImportantCSS(style, `.wptb-table-${tableId}:not(#wptb-style-isolation):not(#wptb-style-isolation)`);
    const styleElement = doc.createElement("style");
    styleElement.id = `wptb-table-${tableId}`;
    styleElement.textContent = scopedStyle;
    styleElement.setAttribute("data-table-id", tableId);
    doc.head.appendChild(styleElement);
  } catch (error) {
    console.error("Error applying custom styles:", error);
  }
};
